import { Modal } from "components/common";
import { ModalProps } from "components/common/Modal";
import {
  StyledErrorMessageWrapper,
  StyledModalMonitorWarningContainer,
  StyledModalMonitorButtonContainer,
  StyledModalMonitorErrorContainer,
  StyledModalMonitorErrorMessage,
  StyledModalMonitorTableContainer,
  StyledMonitorModalInner,
  StyledTitle,
  StyledColorBox,
} from "pages/MonitorManagement/MonitorManageModal/styled";
import { Typography } from "components/parts/Typography/Typography";
import { DisplayModalMonitor } from "pages/MonitorManagement/hook";
import {
  StyledNoHoverTbodyTr,
  StyledTable,
  StyledTbody,
  StyledTbodyTd,
  StyledThead,
  StyledTheadTh,
  StyledTheadTr,
  TableColumnProps,
} from "components/templates/Table/styled";
import React from "react";
import { Button } from "components/parts/Button/Button";
import { useConsoleState } from "providers/ConsoleStateProvider";
import {
  ChangeModalObject,
  ErrorModalMessageObject,
  MONITOR_MODAL_INTERVIEW_ANS_AND_DATE_COLUMN_NO,
  NewlyModalObject,
  WarningModalMessageObject,
} from "pages/MonitorManagement/service";
import { newColors } from "constants/newColors";
import {
  StyledModalInstruction,
  StyledModalInstructionContainer,
} from "pages/MonitorManagement/MonitorManageModal/styled";

type MonitorManageModalProps = {
  confirmMonitorList: DisplayModalMonitor[];
  onClickImportButton: () => void;
  onClickCancelButton: () => void;
  errorList: ErrorModalMessageObject[];
  warningList: WarningModalMessageObject[];
  changeList: ChangeModalObject[];
  newlyList: NewlyModalObject[];
  importButtonDisabled: boolean;
} & ModalProps;

export const MonitorManageModal: React.FC<MonitorManageModalProps> = ({
  closeHandler,
  confirmMonitorList,
  onClickImportButton,
  onClickCancelButton,
  errorList,
  warningList,
  changeList,
  newlyList,
  importButtonDisabled,
}) => {
  const { isPersonalDataHandler } = useConsoleState();

  const potentialEmailColumnNo = 3;
  const otherChangeList = changeList.filter(
    ({ columnNo }) =>
      columnNo === potentialEmailColumnNo + (isPersonalDataHandler ? 3 : 2)
  );
  const interviewSeqColumnNo =
    potentialEmailColumnNo + (isPersonalDataHandler ? 1 : 0);
  const interviewDateTimeColumnNo =
    potentialEmailColumnNo + (isPersonalDataHandler ? 2 : 1);

  const confirmMonitorTableColumn = [
    {
      columnId: "No",
      columnType: "typography",
      textAlign: "center",
      minWidth: "3em",
      text: "No",
      width: "1000px",
    },
    {
      columnId: "ansId",
      columnType: "typography",
      textAlign: "center",
      minWidth: "5em",
      text: "管理ID",
      width: "1000px",
    },
    {
      columnId: "name",
      columnType: "typography",
      textAlign: "left",
      minWidth: "8em",
      text: "名前",
      width: "1000px",
    },
    isPersonalDataHandler && {
      columnId: "email",
      columnType: "typography",
      textAlign: "left",
      minWidth: "15em",
      text: "メールアドレス",
      width: "1000px",
    },
    {
      columnId: "assignedInterviewSeq",
      columnType: "typography",
      textAlign: "center",
      minWidth: "4em",
      text: "実査No.",
      width: "1000px",
    },
    {
      columnId: "assignedInterview",
      columnType: "typography",
      textAlign: "left",
      minWidth: "21em",
      text: "アサインチャットルーム",
      width: "2000px",
    },
    {
      columnId: "otherChanges",
      columnType: "typography",
      textAlign: "left",
      minWidth: "15em",
      text: "その他の変更",
      width: "2000px",
    },
  ].filter((column) => column) as TableColumnProps[];

  return (
    <Modal height="85%" width="min-content" closeHandler={closeHandler}>
      <StyledMonitorModalInner>
        <StyledTitle text="モニター管理" size="large" />
        <StyledErrorMessageWrapper>
          {errorList.length > 0 ? (
            <Typography
              color="errorText"
              text={`${errorList.length}件のエラーが見つかりました。`}
            />
          ) : changeList.length === 0 && newlyList.length === 0 ? (
            <Typography text={"変更点が見つかりませんでした。"} />
          ) : (
            <></>
          )}
        </StyledErrorMessageWrapper>
        {errorList.length > 0 && (
          <StyledModalMonitorErrorContainer>
            <ul>
              {errorList.map(({ message }, index) => (
                <StyledModalMonitorErrorMessage key={index}>
                  <Typography text={message} />
                </StyledModalMonitorErrorMessage>
              ))}
            </ul>
          </StyledModalMonitorErrorContainer>
        )}
        {warningList.length > 0 && (
          <StyledModalMonitorWarningContainer>
            <ul>
              {warningList.map(({ message }, index) => (
                <StyledModalMonitorErrorMessage key={index}>
                  <Typography text={message} />
                </StyledModalMonitorErrorMessage>
              ))}
            </ul>
          </StyledModalMonitorWarningContainer>
        )}
        <StyledModalInstructionContainer>
          <StyledModalInstruction>
            <StyledColorBox background={newColors.errorZone} />
            <Typography text="：エラー" />
          </StyledModalInstruction>
          {isPersonalDataHandler && (
            <StyledModalInstruction>
              <StyledColorBox background={newColors.warningZone} />
              <Typography text="：警告" />
            </StyledModalInstruction>
          )}
          <StyledModalInstruction>
            <StyledColorBox background={newColors.changeZone} />
            <Typography text="：変更" />
          </StyledModalInstruction>
          <StyledModalInstruction>
            <StyledColorBox background={newColors.newlyZone} />
            <Typography text="：新規" />
          </StyledModalInstruction>
        </StyledModalInstructionContainer>
        <StyledModalMonitorTableContainer>
          <StyledTable>
            <StyledThead>
              <StyledTheadTr>
                {confirmMonitorTableColumn.map((column) => (
                  <StyledTheadTh
                    key={`tableHeaderColumn${column.columnId}`}
                    textAlign={column.textAlign}
                    width={column.width}
                    minWidth={column.minWidth}
                  >
                    <Typography text={column.text} />
                  </StyledTheadTh>
                ))}
              </StyledTheadTr>
            </StyledThead>
            <StyledTbody>
              {confirmMonitorList.length !== 0 &&
                confirmMonitorList.map((monitor, rowIndex) => (
                  <StyledNoHoverTbodyTr
                    key={monitor.id}
                    style={
                      newlyList.some(({ no }) => no === rowIndex + 1)
                        ? { background: newColors.newlyZone }
                        : {}
                    }
                  >
                    {confirmMonitorTableColumn.map((column, columnIndex) => (
                      <StyledTbodyTd
                        key={`${column.columnId}Of${monitor.id}`}
                        textAlign={column.textAlign}
                        minWidth={column.minWidth}
                        width={column.width}
                        style={
                          errorList.some(
                            ({ no, columnNo }) =>
                              (no === rowIndex + 1 &&
                                columnNo ===
                                  MONITOR_MODAL_INTERVIEW_ANS_AND_DATE_COLUMN_NO &&
                                (columnIndex === interviewSeqColumnNo ||
                                  columnIndex === interviewDateTimeColumnNo)) ||
                              (no === rowIndex + 1 && columnNo === columnIndex)
                          )
                            ? { background: newColors.errorZone }
                            : warningList.some(
                                ({ no, columnNo }) =>
                                  no === rowIndex + 1 &&
                                  columnNo === columnIndex
                              )
                            ? { background: newColors.warningZone }
                            : changeList.some(
                                ({ no, columnNo }) =>
                                  no === rowIndex + 1 &&
                                  columnNo === columnIndex
                              )
                            ? { background: newColors.changeZone }
                            : {}
                        }
                      >
                        {column.columnId === "otherChanges" ? (
                          <>
                            {
                              <Typography
                                text={otherChangeList
                                  .filter(({ no }) => no === rowIndex + 1)
                                  .map(({ columnString }) => columnString)
                                  .join("、")}
                              />
                            }
                          </>
                        ) : (
                          (() => {
                            const key = column.columnId as keyof typeof monitor;
                            const monitorContent = monitor[key];
                            if (typeof monitorContent === "string")
                              return <Typography text={monitorContent} />;
                          })()
                        )}
                      </StyledTbodyTd>
                    ))}
                  </StyledNoHoverTbodyTr>
                ))}
            </StyledTbody>
          </StyledTable>
        </StyledModalMonitorTableContainer>
        <StyledModalMonitorButtonContainer>
          <Button
            onClick={onClickCancelButton}
            text="キャンセル"
            variant={"secondary"}
          />
          <Button
            onClick={onClickImportButton}
            text="インポート"
            disabled={importButtonDisabled}
          />
        </StyledModalMonitorButtonContainer>
      </StyledMonitorModalInner>
    </Modal>
  );
};
